import React, { useEffect, useState } from 'react';
import { getNewArrivals, getFeaturedProducts, getTopSelling } from '../services/productService';
import HomeCarousel from '../components/HomeCarousel';
import DashboardHeader from '../components/DashboardHeader';
import Toast from '../components/Toast';
import TermsModal from '../components/TermsModal';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Home.css';
import '../styles/pageAnimation.css';

function Home() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [topSelling, setTopSelling] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();

  // Redirect admins to product management
  useEffect(() => {
    const role = localStorage.getItem('role');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (role === 'admin' || isAdmin) {
      navigate('/admin/products', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [latest, featured, top] = await Promise.all([
          getNewArrivals(), 
          getFeaturedProducts(),
          getTopSelling()
        ]);
        setNewArrivals(latest || []); // truly new products by createdAt
        setFeaturedItems(featured || []); // products marked as featured
        setTopSelling(top || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchData();
  }, []);

  // responsive mobile check
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for cart update events from ProductCard
  useEffect(() => {
    const handleCartUpdated = async (event) => {
      // Check if the event has detail about the operation
      if (event.detail) {
        const { action, productName } = event.detail;
        if (action === 'added') {
          setToastMessage(`${productName} added to cart!`);
          setShowToast(true);
        } else if (action === 'removed') {
          setToastMessage(`${productName} removed from cart!`);
          setShowToast(true);
        }
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdated);
    return () => window.removeEventListener('cartUpdated', handleCartUpdated);
  }, []);

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', padding: '0 2vw' }}>
          <div className="home">
            {/* Hero Section */}
            <motion.section 
              className="hero"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
            <div className="hero-content-wrapper">
              <div className="hero-text">
                <motion.h1 
                  className="hero-title"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  style={{ marginBottom: '50px' }} // enlarged gap
                >
                  Fresh Finds <span className="hero-title-highlight">Waiting</span> for You
                </motion.h1>
                <motion.p 
                  className="hero-description"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
                >
                  Explore our wide range of garden essentials, crafted to bring beauty,
                  life, and relaxation to every corner of your home.
                </motion.p>
                <motion.div 
                  className="hero-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7, ease: [0.4, 0, 0.2, 1] }}
                >
                  <button 
                    className="shop-now" 
                    onClick={() => navigate('/catalog')}
                  >
                    <span>Shop Now</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </button>
                  <motion.button 
                    className="learn-more" 
                    onClick={() => navigate('/about')}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    About Us
                  </motion.button>
                </motion.div>
                <motion.div 
                  className="hero-stats"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.8, ease: [0.4, 0, 0.2, 1] }}
                >
                  {[
                    { number: '100+', label: 'Products' },
                    { number: '100%', label: 'Organic' },
                    { number: 'Wide', label: 'Selection' }
                  ].map((stat, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <div className="stat-divider"></div>}
                      <motion.div 
                        className="stat-item"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.9 + (index * 0.1) }}
                      >
                        <span className="stat-number">{stat.number}</span>
                        <span className="stat-label">{stat.label}</span>
                      </motion.div>
                    </React.Fragment>
                  ))}
                </motion.div>
              </div>

              <motion.div 
                className="hero-image-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="hero-image-backdrop"></div>
                <div className="hero-image-glow"></div>
                <img src="/images/chiquis-garden_transparentlogo.png" alt="Chiqui's Garden" className="hero-logo" />
              </motion.div>
            </div>
          </motion.section>
          </div>
        </div>

        {/* Category Preview Section */}
        <motion.section 
          className="categories-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        >
            <motion.h2 
              className="categories-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Shop by Category
            </motion.h2>
            <div className="categories-grid">
              {[
                {
                  name: 'Indoor Plants',
                  desc: 'Perfect for your home',
                  category: 'Indoor Plants',
                  icon: (
                    <svg className="category-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M32 8C32 8 24 16 24 24C24 28.4 27.6 32 32 32C36.4 32 40 28.4 40 24C40 16 32 8 32 8Z" fill="#22c55e"/>
                      <path d="M32 20C32 20 28 24 28 28C28 30.2 29.8 32 32 32C34.2 32 36 30.2 36 28C36 24 32 20 32 20Z" fill="#16a34a"/>
                      <rect x="26" y="32" width="12" height="20" rx="2" fill="#8b4513"/>
                      <ellipse cx="32" cy="54" rx="14" ry="4" fill="#d4a574"/>
                    </svg>
                  )
                },
                {
                  name: 'Soil',
                  desc: 'Organic & Garden',
                  category: 'Soil',
                  icon: (
                    <svg className="category-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="8" y="24" width="48" height="28" rx="4" fill="#8b7355"/>
                      <circle cx="16" cy="32" r="3" fill="#654321"/>
                      <circle cx="28" cy="36" r="2.5" fill="#654321"/>
                      <circle cx="40" cy="34" r="3" fill="#654321"/>
                      <circle cx="48" cy="40" r="2" fill="#654321"/>
                      <circle cx="20" cy="44" r="2.5" fill="#654321"/>
                      <circle cx="36" cy="44" r="2" fill="#654321"/>
                      <path d="M32 12C32 12 28 16 28 20C28 22.2 29.8 24 32 24C34.2 24 36 22.2 36 20C36 16 32 12 32 12Z" fill="#22c55e"/>
                    </svg>
                  )
                },
                {
                  name: 'Pots',
                  desc: 'Stylish containers',
                  category: 'Pots',
                  icon: (
                    <svg className="category-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 24L14 52C14 54 15 56 17 56H47C49 56 50 54 50 52L46 24H18Z" fill="#cd7f32"/>
                      <path d="M18 24L14 52C14 54 15 56 17 56H47C49 56 50 54 50 52L46 24H18Z" fill="url(#potGradient)"/>
                      <ellipse cx="32" cy="24" rx="14" ry="4" fill="#8b5a2b"/>
                      <rect x="16" y="22" width="32" height="4" rx="2" fill="#654321"/>
                      <defs>
                        <linearGradient id="potGradient" x1="32" y1="24" x2="32" y2="56" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#d4a574" stopOpacity="0.3"/>
                          <stop offset="1" stopColor="#8b5a2b" stopOpacity="0.5"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  )
                },
                {
                  name: 'Stones',
                  desc: 'Boulders & Stepping',
                  category: 'Stones',
                  icon: (
                    <svg className="category-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="32" cy="48" rx="28" ry="6" fill="#b0b0b0" opacity="0.3"/>
                      <path d="M20 36C20 36 16 38 16 42C16 46 18 48 22 48C26 48 28 46 28 42C28 38 24 36 20 36Z" fill="#8b8b8b"/>
                      <path d="M44 36C44 36 40 38 40 42C40 46 42 48 46 48C50 48 52 46 52 42C52 38 48 36 44 36Z" fill="#8b8b8b"/>
                      <path d="M32 28C32 28 26 30 26 36C26 42 28 46 32 46C36 46 38 42 38 36C38 30 32 28 32 28Z" fill="#a0a0a0"/>
                      <circle cx="20" cy="40" r="1.5" fill="#696969"/>
                      <circle cx="32" cy="34" r="2" fill="#696969"/>
                      <circle cx="44" cy="40" r="1.5" fill="#696969"/>
                    </svg>
                  )
                }
              ].map((category, index) => (
                <motion.div
                  key={category.name}
                  className="category-card"
                  onClick={() => { 
                    navigate(`/catalog?category=${encodeURIComponent(category.category)}`); 
                  }}
                  style={{ cursor: 'pointer' }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.1), ease: [0.4, 0, 0.2, 1] }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="category-image">
                    {category.icon}
                  </div>
                  <h3 className="category-name">{category.name}</h3>
                  <p className="category-desc">{category.desc}</p>
                </motion.div>
              ))}
            </div>
        </motion.section>

        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', padding: '0 2vw', marginTop: 0 }}>
          <div className="home" style={{ marginTop: 0, paddingTop: 0 }}>
            {/* New Arrivals */}
            <motion.section 
              className="collection-section new-arrivals-collection"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            >
            <div className="collection-container">
              {/* Section Header */}
              <motion.div 
                className="collection-header"
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h2 className="collection-title">Freshly Planted Arrivals</h2>
                <p className="collection-subtitle">Brand new plants, pots, and garden essentials just added to our collection</p>
              </motion.div>

              {/* Products Grid */}
              <HomeCarousel products={newArrivals.slice(0, 4)} title="new-arrivals" />

              {/* View All Button */}
              <motion.div 
                className="collection-footer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.button
                  onClick={() => navigate('/catalog?filter=newArrivals')}
                  className="view-all-btn new-btn"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>View New Arrivals</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </motion.button>
              </motion.div>
            </div>
          </motion.section>

          {/* Featured Items */}
          <motion.section 
            className="collection-section featured-collection"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="collection-container">
              {/* Section Header */}
              <motion.div 
                className="collection-header"
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h2 className="collection-title">Featured Favorites</h2>
                <p className="collection-subtitle">Handpicked essentials loved by gardeners and carefully selected by our experts</p>
              </motion.div>

              {/* Products Grid */}
              <HomeCarousel products={featuredItems.slice(0, 4)} title="featured" />

              {/* View All Button */}
              <motion.div 
                className="collection-footer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.button
                  onClick={() => navigate('/catalog?filter=featured')}
                  className="view-all-btn featured-btn"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>View Featured Items</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </motion.button>
              </motion.div>
            </div>
          </motion.section>

          {/* Top Selling */}
          <motion.section 
            className="collection-section trending-collection"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="collection-container">
              {/* Section Header */}
              <motion.div 
                className="collection-header"
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h2 className="collection-title">Top Selling Products</h2>
                <p className="collection-subtitle">Our best-selling products loved by thousands of satisfied customers</p>
              </motion.div>

              {/* Products Grid */}
              <HomeCarousel products={topSelling.slice(0, 4)} title="top-selling" />

              {/* View All Button */}
              <motion.div 
                className="collection-footer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.button
                  onClick={() => navigate('/catalog?filter=topSelling')}
                  className="view-all-btn trending-btn"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>View Top Selling</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </motion.button>
              </motion.div>
            </div>
          </motion.section>
          </div>
        </div>
      </div>
        
      {/* Footer */}
      <footer style={{
        width: '100%',
        background: 'linear-gradient(135deg, #0f4527 0%, #145a34 50%, #0f4527 100%)',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.facebook.com/profile.php?id=61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.messenger.com/t/61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="viber://chat?number=639368983053"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />

      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
    </div>
  );
}

export default Home;
